import { checkIsProjectWithJobType } from "@repo/app-types/entities/project";
import { getIsTrainingProject } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { Link } from "@repo/ui/Link/index";
import { SimpleModalVariant } from "@repo/ui/Modal/modal";
import { FullPageSkeleton } from "@repo/ui/Skeletons/skeletons";
import { differenceInDays } from "date-fns";
import { useEffect, type FC } from "react";
import { useNavigate, useParams } from "react-router";
import {
  fetchNonEmptyInterviewsForProject,
  selectInterviewsParameters,
} from "../features/interviews/interviewsSlice.js";
import {
  fetchProjectById,
  selectProjectById,
} from "../features/projects/projectsSlice.js";
import { selectTeammate } from "../features/teammate/teammateSlice.js";
import { useAppDispatch, useAppSelector } from "../hooks/hook.js";
import { ProjectDetailsPage } from "./projectDetailsPage.js";
import { TrainingProjectDetailsPage } from "./trainingProjectDetailsPage.js";

export const ProjectDetailsPageContainer: FC = () => {
  const { projectId } = useParams();
  if (!projectId) {
    throw new Error("Missing projectId param");
  }

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const teammate = useAppSelector(selectTeammate);
  const project = useAppSelector((state) =>
    selectProjectById(state, projectId),
  );
  const projectStatus = useAppSelector((state) => state.projects.status);
  const projectError = useAppSelector((state) => state.projects.error);
  const interviewsStatus = useAppSelector((state) => state.interviews.status);
  const interviewsError = useAppSelector((state) => state.interviews.error);
  const isTrainingProject = !!project && getIsTrainingProject(project);

  useEffect(() => {
    dispatch(fetchProjectById(projectId));
  }, [projectId, dispatch]);

  // Once the project is loaded and the active tab is rendered, prefetch interviews if necessary.
  const loadedProjectId = project?.id;
  useEffect(() => {
    // We don't need interviews for training projects.
    if (loadedProjectId === undefined || isTrainingProject) {
      return;
    }

    dispatch((dispatch, getState) => {
      const parameters = selectInterviewsParameters(getState());

      // If the active tab is the interviews tab, it should've already started fetching interviews.
      if (parameters?.projectId !== loadedProjectId) {
        dispatch(
          fetchNonEmptyInterviewsForProject({
            projectId: loadedProjectId,
            page: 1,
          }),
        );
      }
    });
  }, [loadedProjectId, dispatch, isTrainingProject]);

  if (
    (projectStatus === "failed" && projectError) ||
    (interviewsStatus === "failed" && interviewsError)
  ) {
    const isRecentSignup =
      teammate &&
      teammate.created_at &&
      differenceInDays(new Date(), new Date(teammate.created_at)) < 7;

    return (
      <div className="p-4">
        <InfoBanner
          title={`Unable to load ${projectError ? "project" : "interview"}`}
          description={
            <>
              {isRecentSignup ? (
                <>
                  You may need an invite to join your team's existing Alpharun
                  organization. Ask your admin to send you an invite or reach
                  out to{" "}
                  <Link href="mailto:support@alpharun.com">
                    support@alpharun.com
                  </Link>
                  {" for help."}
                </>
              ) : (
                <>
                  Please try again or contact{" "}
                  <Link href="mailto:support@alpharun.com">
                    support@alpharun.com
                  </Link>{" "}
                  if the issue persists.
                </>
              )}
              <div className="mt-4 flex space-x-4">
                <Button
                  variant={ButtonVariant.Secondary}
                  onClick={() => {
                    navigate("/projects");
                  }}
                >
                  Back Home
                </Button>
              </div>
            </>
          }
          variant={SimpleModalVariant.Warning}
        />
      </div>
    );
  }

  // If we're loading but already have a project, don't show loader to avoid UI flash
  if (projectStatus !== "succeeded" && !project) {
    return <FullPageSkeleton />;
  }

  if (!project) {
    return <div>Project not found</div>;
  }

  if (getIsTrainingProject(project)) {
    // All training projects should have a job type.
    if (!checkIsProjectWithJobType(project)) {
      return <div>Job type not found</div>;
    }

    return (
      <TrainingProjectDetailsPage
        // Reset component state when switching projects
        key={project.id}
        project={project}
      />
    );
  }

  return (
    <ProjectDetailsPage
      // Reset component state when switching projects
      key={project.id}
      project={project}
    />
  );
};
